import { AnalysisResult } from '../types';

/**
 * Heuristic detection of "fetch-failed" analysis rows.
 *
 * Fetch-failed URLs have no dedicated flag on stored analysis rows — they are
 * sent to the LLM with `CONTENT: [ERROR] ...` and persisted as regular unmatched
 * rows. We therefore detect them by scanning the reasoning / content summary text
 * for the patterns that surface when the content could not be fetched.
 *
 * This is intentionally a text heuristic (the LLM authors the text, so matches
 * are not guaranteed). The clean long-term fix is a real fetch-failed flag on the
 * stored rows; that is out of scope here.
 */
const FETCH_FAILURE_PATTERNS: string[] = [
  'failed to fetch',
  'fetch error',
  '[error]',
  'could not be fetched',
  'no content',
];

export const isFetchFailureRow = (
  row: Pick<AnalysisResult, 'reasoning' | 'contentSummary'>
): boolean => {
  const haystack = `${row.reasoning ?? ''} ${row.contentSummary ?? ''}`.toLowerCase();
  return FETCH_FAILURE_PATTERNS.some((pattern) => haystack.includes(pattern));
};

/**
 * Filter out fetch-failed rows from a set of analysis samples.
 */
export const filterFetchFailures = <T extends Pick<AnalysisResult, 'reasoning' | 'contentSummary'>>(
  rows: T[]
): T[] => rows.filter((row) => !isFetchFailureRow(row));
